import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Send, Loader2 } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useFormValidation } from '../hooks/useFormValidation';
import { FormsApi } from '../services/formsApi';
import { FormMetadata } from '../types/form.types';
import { FormTheme } from '../types/formTheme';
import FormStepper from './FormStepper';
import FormRenderer from './FormRenderer';

interface MultiStepFormProps {
    formId: string;
    onSuccess?: (submissionId: string, message: string) => void;
    onError?: (error: string) => void;
    onSubmitError?: (error: any) => boolean | void;
    metadata?: FormMetadata;
    recaptchaToken?: string;
    getRecaptchaToken?: () => Promise<string | undefined>;
    surveyToken?: string;
    appearance?: FormTheme;
    className?: string;
}

const MultiStepForm: React.FC<MultiStepFormProps> = ({
    formId,
    onSuccess,
    onError,
    onSubmitError,
    metadata,
    recaptchaToken,
    getRecaptchaToken,
    surveyToken,
    appearance = 'default',
    className = ''
}) => {
    const {
        state,
        setFormDefinition,
        updateSingleResponse,
        setErrors,
        clearError,
        setSubmitting,
        setLoading,
        nextStep,
        previousStep,
        getCurrentStep,
        isFirstStep,
        isLastStep,
        resetForm
    } = useFormState(formId);

    const { validateFields, getVisibleFieldIds } = useFormValidation();
    const [visibleFieldIds, setVisibleFieldIds] = useState<string[]>([]);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const OTHER_TEXT_SUFFIX = '__other_text';

    // Load form definition on mount
    useEffect(() => {
        const initializeForm = async () => {
            try {
                setLoading(true);

                // Fetch form definition
                const definition = await FormsApi.getFormDefinition(formId);
                setFormDefinition(definition);
            } catch (error: any) {
                console.error('Failed to initialize form:', error);
                onError?.(error.message || 'Failed to load form');
            } finally {
                setLoading(false);
            }
        };

        initializeForm();
    }, [formId]);

    // Update visible fields when responses change
    useEffect(() => {
        const currentStep = getCurrentStep();
        if (currentStep) {
            const visible = getVisibleFieldIds(currentStep.fields, state.responses);
            setVisibleFieldIds(visible);
        }
    }, [state.responses, state.currentStepIndex, state.formDefinition]);

    const handleFieldChange = useCallback((fieldId: string, value: string | number | string[]) => {
        updateSingleResponse(fieldId, value);
        clearError(fieldId);
    }, [updateSingleResponse, clearError]);

    const buildApiResponses = useCallback((responses: { [key: string]: any }) => {
        const strippedResponses = Object.entries(responses).reduce((acc, [key, value]) => {
            if (key.endsWith(OTHER_TEXT_SUFFIX)) {
                return acc;
            }
            acc[key] = value;
            return acc;
        }, {} as { [key: string]: any });

        if (!state.formDefinition) {
            return strippedResponses;
        }

        const fieldById = new Map(
            state.formDefinition.steps.flatMap((step) =>
                step.fields.map((field) => [field.fieldId, field] as const)
            )
        );

        const transformed = { ...strippedResponses };

        Object.entries(strippedResponses).forEach(([fieldId, fieldValue]) => {
            const field = fieldById.get(fieldId);
            if (!field?.validation?.allowOther || !field.options?.length) {
                return;
            }

            const otherOption = field.options.find((option) => {
                const optionValue = option.value.trim().toLowerCase();
                const optionLabel = option.label.trim().toLowerCase();
                return optionValue === 'other' || optionLabel === 'other';
            });

            if (!otherOption) {
                return;
            }

            const otherTextKey = `${fieldId}${OTHER_TEXT_SUFFIX}`;
            const otherTextValue = responses[otherTextKey];
            const customOtherText = typeof otherTextValue === 'string' ? otherTextValue.trim() : '';
            const mappedOtherValue = customOtherText.length > 0 ? `other:${customOtherText}` : 'other';

            const mapValue = (value: string | number) =>
                String(value) === otherOption.value ? mappedOtherValue : value;

            if (Array.isArray(fieldValue)) {
                transformed[fieldId] = fieldValue.map((value) => mapValue(value));
                return;
            }

            if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
                transformed[fieldId] = mapValue(fieldValue);
            }
        });

        return transformed;
    }, [state.formDefinition]);

    const handleValidateStep = async (): Promise<boolean> => {
        const currentStep = getCurrentStep();
        if (!currentStep) return false;
        const apiResponses = buildApiResponses(state.responses);

        // Client-side validation
        const errors = validateFields(currentStep.fields, state.responses, visibleFieldIds);

        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            return false;
        }

        // Server-side validation (optional)
        try {
            const result = await FormsApi.validateStep(
                formId,
                currentStep.stepId,
                apiResponses
            );

            if (!result.valid) {
                setErrors(result.errors);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Validation error:', error);
            return true; // Continue on validation API error
        }
    };

    const handleNext = async () => {
        if (isUploadingFiles) return;

        const isValid = await handleValidateStep();
        if (!isValid) return;

        if (isLastStep) {
            await handleSubmit();
        } else {
            nextStep();
            scrollFormToTop();
        }
    };

    const handleBack = () => {
        previousStep();
        scrollFormToTop();
    };

    const scrollFormToTop = () => {
        const scrollContainer = document.querySelector('[data-form-scroll-container="true"]');
        if (scrollContainer instanceof HTMLElement) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getUploadedFileIdsFromResponses = (): string[] => {
        if (!state.formDefinition) {
            return [];
        }

        const fileFieldIds = state.formDefinition.steps.flatMap((step) =>
            step.fields
                .filter((field) => field.type.trim().toLowerCase() === 'file')
                .map((field) => field.fieldId)
        );

        const uploadedFileIds = new Set<string>();
        fileFieldIds.forEach((fieldId) => {
            const value = state.responses[fieldId];
            if (Array.isArray(value)) {
                value.forEach((fileId) => {
                    if (typeof fileId === 'string' && fileId.trim().length > 0) {
                        uploadedFileIds.add(fileId);
                    }
                });
            } else if (typeof value === 'string' && value.trim().length > 0) {
                uploadedFileIds.add(value);
            }
        });

        return Array.from(uploadedFileIds);
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);

            // Get reCAPTCHA token if callback is provided
            let token = recaptchaToken;
            if (getRecaptchaToken) {
                token = await getRecaptchaToken();
            }

            const sanitizedResponses = buildApiResponses(state.responses);

            const response = await FormsApi.submitForm(formId, {
                responses: sanitizedResponses,
                metadata: {
                    ...metadata,
                    referrer: document.referrer,
                    utmSource: new URLSearchParams(window.location.search).get('utm_source') || undefined,
                    utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
                    utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined
                },
                recaptchaToken: token,
                ...(surveyToken ? { surveyToken } : {})
            });

            onSuccess?.(response.submissionId, response.message);
            resetForm();
        } catch (error: any) {
            console.error('Form submission error:', error);
            const hasExpiredUploads =
                error?.type === 'validation_error' &&
                typeof error?.message === 'string' &&
                /uploads?\s+have\s+expired|re-?upload\s+your\s+files/i.test(error.message);

            if (hasExpiredUploads) {
                const uploadedFileIds = getUploadedFileIdsFromResponses();
                if (uploadedFileIds.length > 0) {
                    await Promise.allSettled(
                        uploadedFileIds.map((fileId) => FormsApi.deleteFile(formId, fileId))
                    );
                }
                resetForm();
                scrollFormToTop();
                onError?.('Your uploaded images have expired and were cleared. Please restart the form and upload your images again.');
                return;
            }

            const submitErrorHandled = onSubmitError?.(error);
            if (submitErrorHandled) {
                return;
            }

            if (error.type === 'validation_error' && error.errors) {
                setErrors(error.errors);
            } else if (error.type === 'rate_limit_error') {
                const hours = Math.ceil((error.retry_after || 3600) / 3600);
                onError?.(`You've reached the submission limit. Please try again in ${hours} hour(s).`);
            } else if (error.type === 'recaptcha_failed') {
                onError?.('Security verification failed. Please refresh the page and try again.');
            } else {
                onError?.(error.message || 'Failed to submit form');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (state.isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className={`w-8 h-8 animate-spin ${appearance === 'tracking' ? 'text-black' : 'text-blue-500'}`} />
            </div>
        );
    }

    if (!state.formDefinition) {
        return (
            <div className="text-center py-12 text-red-600">
                Failed to load form. Please refresh the page.
            </div>
        );
    }

    const currentStep = getCurrentStep();
    if (!currentStep) return null;
    const fileFieldIds = state.formDefinition.steps.flatMap((step) =>
        step.fields
            .filter((field) => field.type.trim().toLowerCase() === 'file')
            .map((field) => field.fieldId)
    );

    return (
        <div className={`multi-step-form ${className}`}>
            {/* Progress indicator */}
            <FormStepper
                steps={state.formDefinition.steps}
                currentStepIndex={state.currentStepIndex}
                appearance={appearance}
                className="mb-8"
            />

            {/* Step title and description */}
            <div className="mb-8">
                <h2 className={`text-2xl md:text-3xl mb-2 ${appearance === 'tracking' ? 'font-headline font-semibold text-black' : 'font-bold text-gray-900'}`}>
                    {currentStep.title}
                </h2>
                {currentStep.description && (
                    <p className={appearance === 'tracking' ? 'text-gray-600 text-base' : 'text-gray-600'}>
                        {currentStep.description}
                    </p>
                )}
            </div>

            {/* Form fields */}
            <FormRenderer
                formId={formId}
                fields={currentStep.fields}
                formConfig={state.formDefinition.config}
                fileFieldIds={fileFieldIds}
                responses={state.responses}
                errors={state.errors}
                visibleFieldIds={visibleFieldIds}
                onFieldChange={handleFieldChange}
                onFileUploadStateChange={setIsUploadingFiles}
                appearance={appearance}
                className="mb-8"
            />

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-4">
                <button
                    type="button"
                    onClick={handleBack}
                    disabled={isFirstStep || state.isSubmitting || isUploadingFiles}
                    className={`
                        flex items-center gap-2 px-6 py-3 rounded-xl
                        font-semibold transition-all duration-200
                        ${isFirstStep
                            ? 'invisible'
                            : appearance === 'tracking'
                                ? 'border border-gray-300 text-gray-700 hover:border-gray-500 hover:bg-gray-50 rounded-lg'
                                : 'border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                        }
                        disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                >
                    <ArrowLeft size={20} />
                    Back
                </button>

                <button
                    type="button"
                    onClick={handleNext}
                    disabled={state.isSubmitting || isUploadingFiles}
                    className={`
                        flex items-center gap-2 px-6 py-3 text-white font-semibold
                        disabled:opacity-50 disabled:cursor-not-allowed
                        transition-all duration-200
                        ${appearance === 'tracking'
                            ? 'rounded-lg bg-black hover:bg-gray-800 shadow-sm'
                            : 'rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 shadow-lg hover:shadow-xl'
                        }
                    `}
                >
                    {isUploadingFiles ? (
                        <>
                            <Loader2 size={20} className="animate-spin" />
                            Uploading files...
                        </>
                    ) : state.isSubmitting ? (
                        <>
                            <Loader2 size={20} className="animate-spin" />
                            Submitting...
                        </>
                    ) : isLastStep ? (
                        <>
                            <Send size={20} />
                            Submit
                        </>
                    ) : (
                        <>
                            Next
                            <ArrowRight size={20} />
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default MultiStepForm;
